import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ListItemNode, ListNode } from '@lexical/list'
import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { ToolbarPlugin, type ToolbarPluginCopy } from 'ComponentLibrary/src/molecules/TextEditor/ToolbarPlugin'
import { SubmitPlugin } from 'ComponentLibrary/src/molecules/TextEditor/SubmitPlugin'
import { HeadingNode } from '@lexical/rich-text'
import { styled } from '@mui/material/styles'
import { Box, Stack, useTheme } from '@mui/material'
import { forwardRef, ReactNode, RefObject, useEffect, useState } from 'react'
import { BaseListItem, ContentChip, FileDropArea, tokens } from 'ComponentLibrary/index'
import CustomTextInput from './CustomTextInput'
import { TempSavePlugin } from './TempSavePlugin'
import { assembleNameWithEmailFallback, User } from 'ComponentLibrary/src/common/users'

import {
    $createBeautifulMentionNode,
    BeautifulMentionComponentProps,
    BeautifulMentionsItem,
    BeautifulMentionsMenuItemProps,
    BeautifulMentionsMenuProps,
    BeautifulMentionsPlugin,
    createBeautifulMentionNode,
} from 'lexical-beautiful-mentions'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createTextNode, $getRoot } from 'lexical'
import { UserBubble } from '../UserBubble'
import { Typography } from 'ComponentLibrary/src/piazza'
import { WarningIcon } from '@phosphor-icons/react'

export const mentionPattern = /@id:(\d+)/g

export type TextEditorProps = {
    onSubmit: (newInput: { message: string; documents: File[]; mentions: number[] }) => void
    onCancel?: () => void
    uploadErrors?: string[]
    initialMarkdownState?: string
    submitVariant: 'inline' | 'toolbar'
    threadId?: string
    dragAreaRef?: RefObject<HTMLDivElement | null>
    mentionOptions?: User[]
    enableHeadings?: boolean
    enableNumberedList?: boolean
    validateTotalAttachmentSize?: boolean
    disableSubmit?: boolean
    copy?: Partial<TextEditorCopy>
}

export type TextEditorCopy = {
    noMentionResults: string
    placeholder: string
    attachmentTooLarge: string
    toolbar: ToolbarPluginCopy
}

const DEFAULT_TEXT_EDITOR_COPY: TextEditorCopy = {
    noMentionResults: 'Keine Ergebnisse',
    placeholder: 'Neue Nachricht',
    attachmentTooLarge: 'Die Anhänge sind zusammen größer als 7,5 MB.',
    toolbar: {
        textStyle: 'Textstil',
        bold: 'Fett',
        italic: 'Kursiv',
        bulletedList: 'Aufzählung',
        numberedList: 'Nummerierte Liste',
        headingOptions: {
            paragraph: 'Text',
            h2: 'Headline 1',
            h3: 'Headline 2',
            h4: 'Headline 3',
            h5: 'Headline 4',
        },
    },
}

// Keep raw attachments below 7.5 MB so the final MIME-encoded SES message stays
// safely under the 10 MiB SendRawEmail limit in typical cases:
// https://docs.aws.amazon.com/ses/latest/APIReference/API_SendRawEmail.html
const MAX_TOTAL_ATTACHMENT_SIZE_IN_BYTES = 7_500_000

export const CustomMentionComponent = forwardRef<HTMLDivElement, BeautifulMentionComponentProps<{ id: string; avatarUrl?: string }>>(
    ({ trigger, data }, ref) => {
        const { name } = data

        return <ContentChip size="small" label={`${trigger}${name}`} color="green" ref={ref} />
    }
)

export const TextEditor = ({
    onSubmit,
    onCancel,
    initialMarkdownState,
    submitVariant,
    threadId,
    mentionOptions,
    dragAreaRef,
    enableHeadings,
    enableNumberedList,
    disableSubmit = false,
    validateTotalAttachmentSize = false,
    copy,
}: TextEditorProps) => {
    const theme = useTheme()
    const resolvedCopy = {
        ...DEFAULT_TEXT_EDITOR_COPY,
        ...copy,
        toolbar: {
            ...DEFAULT_TEXT_EDITOR_COPY.toolbar,
            ...copy?.toolbar,
            headingOptions: {
                ...DEFAULT_TEXT_EDITOR_COPY.toolbar.headingOptions,
                ...copy?.toolbar?.headingOptions,
            },
        },
    }
    const [isOver, setIsOver] = useState(false)

    const [newDocuments, setNewDocuments] = useState<File[]>([])
    const hasAttachmentSizeError =
        validateTotalAttachmentSize &&
        newDocuments.reduce((total, document) => total + document.size, 0) > MAX_TOTAL_ATTACHMENT_SIZE_IN_BYTES

    const submitMessageWithAttachmentsAndMentions = (message: string, mentions: number[]) => {
        onSubmit({ message, documents: newDocuments, mentions })
        setNewDocuments([])
    }

    const selectFiles = (files: File[]) => setNewDocuments((prev) => [...prev, ...files])

    const MentionsListContainer = ({ children }: { children: ReactNode }) => {
        return (
            <Box
                width={'278px'}
                component={'ul'}
                sx={{
                    position: 'absolute',
                    zIndex: 9999,
                    backgroundColor: tokens.ColorSurfaceDefault,
                    boxShadow: tokens.Flyout,
                    bottom: tokens.SizingSpacing600,
                    borderRadius: tokens.SizingCornerS,
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                }}
            >
                {children}
            </Box>
        )
    }

    const EmptyMentionsList = () => {
        return (
            <MentionsListContainer>
                <BaseListItem title={resolvedCopy.noMentionResults} padding={`0px ${tokens.SizingSpacing150}`} height={'32px'} />
            </MentionsListContainer>
        )
    }

    const lexicalTheme = {
        text: {
            bold: 'lexical-editor-bold',
            italic: 'lexical-editor-italic',
            color: 'red',
        },
        '@': { position: 'absolute', zIndex: 9999 },
    }

    function CustomMenu({ ...props }: BeautifulMentionsMenuProps) {
        return <MentionsListContainer>{props.children}</MentionsListContainer>
    }

    const CustomMenuItem = forwardRef<HTMLLIElement, BeautifulMentionsMenuItemProps>(({ selected, item, ...props }, ref) => {
        const SelectableListItemStyle = {
            cursor: 'pointer',
            borderRadius: '0px',
            border: 0,
            background: 'none',
            '&:hover': {
                backgroundColor: tokens.ColorBackgroundDark,
            },
        }

        const SelectedStyling = {
            '&:not(:active)': {
                backgroundColor: tokens.ColorBackgroundDark,
            },
        }

        const NonSelectedStyling = {
            '&:focus': {
                backgroundColor: tokens.ColorSurfaceHover,
            },
        }

        const currentUser = mentionOptions?.find((user) => user.id === item.data.id)

        return (
            <Stack
                component={'li'}
                ref={ref}
                minHeight={'32px'}
                padding={tokens.SizingSpacing050}
                direction="row"
                alignItems="center"
                gap={tokens.SizingSpacing075}
                aria-selected={selected}
                sx={selected ? { ...SelectableListItemStyle, ...SelectedStyling } : { ...SelectableListItemStyle, ...NonSelectedStyling }}
                {...props}
            >
                <UserBubble
                    size={tokens.SizingIconM}
                    profileImageUrl={currentUser?.profile_thumbnail_image_url}
                    profileFallbackImageUrl={currentUser?.profile_image_url}
                />
                <Stack direction="column">
                    <Typography variant={'textS'} color={tokens.ColorTextBold}>
                        {item.data.name}
                    </Typography>
                    {currentUser && (
                        <Typography variant={'graphLabel'} color={tokens.ColorTextDefault}>
                            {currentUser.organization_name}
                        </Typography>
                    )}
                </Stack>
            </Stack>
        )
    })

    const initialState = initialMarkdownState ? () => $convertFromMarkdownString(initialMarkdownState, TRANSFORMERS) : undefined
    const initialConfig = {
        namespace: 'RichTextEditor',
        theme: lexicalTheme,
        nodes: [ListNode, ListItemNode, HeadingNode, ...createBeautifulMentionNode(CustomMentionComponent)],
        editorState: initialState,
        onError: (error: Error) => {
            throw error
        },
    }

    function MentionsTransformerPlugin({ users }: { users?: User[] }) {
        const [editor] = useLexicalComposerContext()

        useEffect(() => {
            const replacePatternWithNode = () => {
                editor.update(() => {
                    const root = $getRoot()
                    const nodes = root.getAllTextNodes()

                    nodes.forEach((node) => {
                        const textContent = node.getTextContent()

                        const matches = [...textContent.matchAll(mentionPattern)]

                        let lastIndex = 0

                        if (matches.length > 0) {
                            matches.forEach((match) => {
                                const [matchedText, id] = match
                                const parsedId = parseInt(id, 10)
                                const startIndex = match.index

                                if (startIndex > lastIndex) {
                                    const plainText = textContent.substring(lastIndex, startIndex)
                                    node.insertBefore($createTextNode(plainText))
                                }

                                const mentioned_user = users?.find((user) => user.id === parsedId)

                                const mentionNode = $createBeautifulMentionNode('@', `id:${parsedId}`, {
                                    id: parsedId,
                                    name: mentioned_user ? assembleNameWithEmailFallback(mentioned_user) : '?',
                                })

                                node.insertBefore(mentionNode)
                                lastIndex = startIndex + matchedText.length
                            })

                            if (lastIndex < textContent.length) {
                                const remainingText = textContent.substring(lastIndex)
                                node.insertBefore($createTextNode(remainingText))
                            }

                            node.remove()
                        }
                    })
                })
            }

            replacePatternWithNode()
        }, [editor])

        return null
    }

    const usersOptions = {
        '@':
            (mentionOptions &&
                mentionOptions?.length > 0 &&
                mentionOptions
                    .filter((user) => user.is_active !== false)
                    .map((user) => {
                        const name = assembleNameWithEmailFallback(user)
                        return {
                            value: `id:${user.id}`,
                            id: user.id,
                            name: `${name}`,
                        }
                    })) ||
            [],
    }

    type MentionsTrigger = '@'

    const handleOnSearch = async (trigger: string, queryString?: string | null): Promise<BeautifulMentionsItem[]> => {
        const items = usersOptions[trigger as MentionsTrigger].sort((a, b) => a.name.localeCompare(b.name)) || []

        if (!queryString) {
            return items
        }

        const normalizedQuery = queryString.toLowerCase()

        // split names from sur-/second- names in order to search in each part
        // 'John Doe' -> ['John', 'Doe']
        // 'John-James Doe' -> ['John', 'James', 'Doe']
        return items.filter((item) => item.name.split(/\W+/).some((word) => word.toLowerCase().startsWith(normalizedQuery)))
    }

    return (
        <LexicalComposer initialConfig={initialConfig}>
            <FileDropArea
                isOver={isOver}
                dragAreaRef={dragAreaRef}
                setIsOver={setIsOver}
                onDataDrop={(_areaId?: string, files?: File[]) => files && selectFiles(files)}
            />
            <EditorWrapper>
                <EditorShell sx={{ background: isOver ? theme.palette.secondary1.grey : undefined }}>
                    <ToolbarPlugin
                        onSubmit={submitVariant === 'toolbar' ? submitMessageWithAttachmentsAndMentions : undefined}
                        onCancel={onCancel}
                        enableHeadings={enableHeadings}
                        enableNumberedList={enableNumberedList}
                        copy={resolvedCopy.toolbar}
                    />
                    <MentionsTransformerPlugin users={mentionOptions} />
                    <RichTextPlugin
                        contentEditable={<CustomTextInput newDocuments={newDocuments} setNewDocuments={setNewDocuments} />}
                        placeholder={<Placeholder>{resolvedCopy.placeholder}</Placeholder>}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <BeautifulMentionsPlugin
                        autoSpace
                        triggers={['@']}
                        menuComponent={CustomMenu}
                        menuItemComponent={CustomMenuItem}
                        insertOnBlur={false}
                        emptyComponent={EmptyMentionsList}
                        onSearch={handleOnSearch}
                    />
                    <TempSavePlugin threadId={threadId} />
                </EditorShell>
                {submitVariant === 'inline' && (
                    <SubmitWrapper>
                        <SubmitPlugin
                            onSubmit={submitMessageWithAttachmentsAndMentions}
                            submitVariant="inline"
                            setSelectedFiles={selectFiles}
                            selectedFiles={newDocuments}
                            disabled={hasAttachmentSizeError || disableSubmit}
                        />
                    </SubmitWrapper>
                )}
            </EditorWrapper>
            {hasAttachmentSizeError && (
                <Stack direction={'row'} spacing={tokens.SizingSpacing100} alignItems={'center'} marginTop={tokens.SizingSpacing100}>
                    <WarningIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorTextDangerSubtle} />
                    <Typography variant="textXS" color={tokens.ColorTextDangerSubtle}>
                        {resolvedCopy.attachmentTooLarge}
                    </Typography>
                </Stack>
            )}
        </LexicalComposer>
    )
}

const Placeholder = styled('div')`
    position: relative;
    max-height: 40px;
    font-size: 16px;
    margin: 12px 14px;
    grid-row: 2;
    grid-column: 1;
    color: #bbb;
    overflow: clip;
    pointer-events: none;
`

const SubmitWrapper = styled('div')`
    margin-top: 48px;
`

const EditorWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`

const EditorShell = styled('div')`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
`
